<template>
  <div class="broker-competitions">
    <!-- Loading Overlay -->
    <LoadingOverlay :loading="isLoading" :message="loadingMessage" />

    <!-- Skeleton Loader for initial load -->
    <SkeletonLoader v-if="isInitialLoading" type="competitions" />

    <!-- Main Content (hidden during initial load) -->
    <div v-show="!isInitialLoading">
      <!-- Header Section -->
      <div class="competitions-header">
        <div class="d-flex justify-space-between align-start">
          <div>
            <h1 class="text-h1 font-weight-bold mb-3">{{ $t('broker.competitions.title') }}</h1>
            <p class="text-body-1 text--darken-2 ma-0">
              {{ $t('broker.competitions.header.subtitle') }}
            </p>
          </div>
          <v-btn outlined rounded color="#005931" :class="{ 'btn-disabled': isReadOnlyStaff }" :disabled="isReadOnlyStaff" @click="!isReadOnlyStaff && createNewCompetition()">
            <v-icon left size="18">fa-regular fa-trophy</v-icon>
            {{ $t('broker.competitions.header.createBtn') }}
          </v-btn>
        </div>
      </div>

      <!-- Active Competitions Section -->
      <div class="active-competitions-section mb-10">
        <div class="section-header">
          <div class="d-flex justify-space-between align-start">
            <div>
              <h2 class="text-h2 font-weight-bold">{{ $t('broker.competitions.activeSection.title') }}</h2>
              <div class="d-flex align-center" style="margin: 20px 0;">
                <span class="text-h3 grey--text">{{ $t('broker.competitions.activeSection.nowLive') }}</span>
                <span class="live-indicator ml-2"></span>
              </div>
            </div>
            <div class="d-flex align-center" style="gap: 16px;">
              <!-- Community Filter -->
              <v-select
                v-model="selectedClubId"
                :items="clubFilterOptions"
                item-text="name"
                item-value="id"
                outlined
                dense
                hide-details
                class="club-filter-select"
                @change="handleClubFilterChange"
                style="min-width: 200px;"
              >
                <template v-slot:prepend-inner>
                  <v-icon size="16" color="grey darken-1">fa-regular fa-users</v-icon>
                </template>
              </v-select>

              <!-- Mode Toggle -->
              <div class="mode-toggle-container">
                <div class="mode-toggle-slider">
                  <div class="slider-background" :class="{ 'demo-active': competitionMode === 'demo' }"></div>
                  <button class="toggle-option" :class="{ 'active': competitionMode === 'live' }"
                    @click="competitionMode = 'live'">
                    <i class="fa-regular fa-bolt"></i>
                    <span>{{ $t('broker.competitions.activeSection.live') }}</span>
                  </button>
                  <button class="toggle-option" :class="{ 'active': competitionMode === 'demo' }"
                    @click="competitionMode = 'demo'">
                    <i class="fa-regular fa-book"></i>
                    <span>{{ $t('broker.competitions.activeSection.demo') }}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Competition Cards Carousel -->
        <div class="competitions-carousel" v-if="activeCompetitions.length">
          <div class="carousel-wrapper">
            <div ref="carouselContainer" class="competition-cards-container" @scroll="handleScroll">
              <CompetitionCard v-for="competition in activeCompetitions" :key="competition.id"
                :competition="competition" :mode="competitionMode" @click="viewCompetition"
                class="competition-card-item" />
            </div>
            <!-- Right Blur Effect -->
            <!-- <div v-if="canScrollRight" class="carousel-blur-right"></div>-->
          </div>

          <!-- Carousel Navigation -->
          <div class="carousel-navigation mt-4">
            <v-btn text :disabled="!canScrollLeft" @click="scrollLeft" class="nav-btn">
              <v-icon size="14">fa-regular fa-chevron-left</v-icon>
            </v-btn>
            <v-btn text :disabled="!canScrollRight" @click="scrollRight" class="nav-btn">
              <v-icon size="14">fa-regular fa-chevron-right</v-icon>
            </v-btn>
          </div>
        </div>

        <!-- No Competitions Message -->
        <div v-else class="empty-state">
          <v-icon size="48" color="grey lighten-1">fa-regular fa-trophy</v-icon>
          <p class="text-h3 grey--text text--lighten-1 mt-4">{{ $t('broker.competitions.empty.active.title', {
            mode:
              competitionMode
          }) }}</p>
        </div>
      </div>

      <!-- Historical Competitions Section -->
      <div class="historical-competitions-section">
        <div class="section-header">
          <div class="d-flex justify-space-between align-start">
            <div>
              <h2 class="text-h2 font-weight-bold">{{ $t('broker.competitions.historicalSection.title') }}</h2>
              <div class="d-flex align-center" style="margin: 20px 0;">
                <span class="text-h3 grey--text">{{ $t('broker.competitions.historicalSection.periodLabel', {
                  period:
                    $t(`broker.competitions.historicalSection.periods.${periodKey}`)
                }) }}</span>
              </div>
            </div>
            <v-select ref="periodSelect" v-model="selectedPeriod" :items="periodOptions" item-text="label"
              item-value="value" outlined dense hide-details class="period-filter-select" @change="handlePeriodChange"
              @blur="forceBlur" />
          </div>
        </div>

        <!-- Historical Competition Cards Carousel -->
        <div class="competitions-carousel" v-if="historicalCompetitions.length">
          <div class="carousel-wrapper">
            <div ref="historicalCarouselContainer" class="competition-cards-container" @scroll="handleScrollHistorical">
              <HistoricalCompetitionCard v-for="competition in historicalCompetitions" :key="competition.id"
                :competition="competition" @click="handleHistoricalClick" class="competition-card-item" />
            </div>
            <!-- Right Blur Effect -->
            <!-- <div v-if="canScrollRightHistorical" class="carousel-blur-right"></div> -->
          </div>

          <!-- Carousel Navigation -->
          <div class="carousel-navigation mt-4">
            <v-btn text :disabled="!canScrollLeftHistorical" @click="scrollLeftHistorical" class="nav-btn">
              <v-icon size="14">fa-regular fa-chevron-left</v-icon>
            </v-btn>
            <v-btn text :disabled="!canScrollRightHistorical" @click="scrollRightHistorical" class="nav-btn">
              <v-icon size="14">fa-regular fa-chevron-right</v-icon>
            </v-btn>
          </div>
        </div>

        <!-- No Historical Competitions -->
        <div v-else class="empty-state">
          <v-icon size="48" color="grey lighten-1">fa-regular fa-history</v-icon>
          <p class="text-h3 grey--text text--lighten-1 mt-4">{{ $t('broker.competitions.empty.historical.title', {
            period: $t(`broker.competitions.historicalSection.periods.${periodKey}`)
          }) }}</p>
        </div>
      </div>
          <!-- Competition Creation Wizard Modal -->
      <div v-if="wizardOpen || fromWizard" class="wizard-modal-backdrop" :class="{ 'closing': !wizardOpen && fromWizard }">
        <div class="wizard-modal-container">
          <Wizard
            :class="'competitions-wizard ' + (wizardOpen ? 'three ' : '') + (!wizardOpen && fromWizard ? 'three out' : '')"
            :fromButton="true" :hideWizard="hideWizard" type="tournament" :clubId="$store.getters['broker/mainClubId']" />
        </div>
      </div>
    </div>


    <transition name="fade">
      <router-view name="overlay" />
    </transition>
  </div>
</template>

<script>
import CompetitionCard from '../components/CompetitionCard.vue'
import HistoricalCompetitionCard from '../components/HistoricalCompetitionCard.vue'
import competitionService from '../services/real-competition.service'
import LoadingOverlay from '../components/LoadingOverlay.vue'
import SkeletonLoader from '../components/SkeletonLoader.vue'
import Wizard from '../../../views/Funding/Simulation/components/Wizard/Wizard.vue'

export default {
  name: 'BrokerCompetitions',
  components: {
    CompetitionCard,
    HistoricalCompetitionCard,
    LoadingOverlay,
    SkeletonLoader,
    Wizard
  },
  data() {
    return {
      // Loading states
      isLoading: false,
      isInitialLoading: true,
      loadingMessage: 'Loading competitions...',
      // Data properties
      competitionMode: 'live',
      selectedPeriod: 'lastQuarter',
      selectedClubId: null, // null means "All Communities"
      loading: false,
      canScrollLeft: false,
      canScrollRight: false,
      canScrollLeftHistorical: false,
      canScrollRightHistorical: false,

      // Competition data
      allCompetitions: [], // Store all unfiltered competitions
      liveCompetitions: [],
      demoCompetitions: [],
      historicalCompetitions: [],
      availableClubs: [], // For club filter dropdown

      // Wizard state
      wizardOpen: false,
      fromWizard: false,

    }
  },
  computed: {
    activeCompetitions() {
      return this.competitionMode === 'live' ? this.liveCompetitions : this.demoCompetitions
    },
    periodOptions() {
      return [
        { label: this.$t('broker.competitions.historicalSection.periods.lastWeek'), value: 'lastWeek' },
        { label: this.$t('broker.competitions.historicalSection.periods.lastMonth'), value: 'lastMonth' },
        { label: this.$t('broker.competitions.historicalSection.periods.lastQuarter'), value: 'lastQuarter' },
        { label: this.$t('broker.competitions.historicalSection.periods.lastYear'), value: 'lastYear' },
        { label: this.$t('broker.competitions.historicalSection.periods.allTime'), value: 'allTime' }
      ]
    },
    clubFilterOptions() {
      return [
        { id: null, name: this.$t('broker.competitions.filters.allCommunities') || 'All Communities' },
        ...this.availableClubs
      ]
    },
    periodKey() {
      // selectedPeriod is now the key directly
      return this.selectedPeriod
    },
    isReadOnlyStaff() {
      return this.$store.getters['broker/isReadOnlyStaff']
    },
  },

  watch: {
    competitionMode() {
      this.$nextTick(() => {
        this.checkScrollButtons()
      })
    },
    historicalCompetitions() {
      this.$nextTick(() => {
        this.checkScrollButtonsHistorical()
      })
    }
  },

  async mounted() {
    this.isInitialLoading = true
    try {
      await this.loadCompetitions()
    } finally {
      // Add a small delay to make the transition smoother
      setTimeout(() => {
        this.isInitialLoading = false
        // Check scroll buttons after skeleton loader is hidden and DOM is updated
        this.$nextTick(() => {
          this.checkScrollButtons()
          this.checkScrollButtonsHistorical()
        })
      }, 500)
    }
    window.addEventListener('resize', this.checkScrollButtons)
    window.addEventListener('resize', this.checkScrollButtonsHistorical)
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.checkScrollButtons)
    window.removeEventListener('resize', this.checkScrollButtonsHistorical)
    document.querySelector("html").classList.remove("modal-open")
  },

  methods: {
    forceBlur() {
      // Force blur when the select loses focus
      if (this.$refs.periodSelect && this.$refs.periodSelect.$el) {
        const input = this.$refs.periodSelect.$el.querySelector('input')
        if (input) {
          input.blur()
        }
      }
    },
    async handlePeriodChange() {
      // Force blur to remove focused state after selection
      // Use setTimeout to ensure it happens after Vuetify's internal state updates
      setTimeout(() => {
        if (this.$refs.periodSelect && this.$refs.periodSelect.$el) {
          const input = this.$refs.periodSelect.$el.querySelector('input')
          if (input) {
            input.blur()
          }
          // Also try to blur the component itself
          if (this.$refs.periodSelect.blur) {
            this.$refs.periodSelect.blur()
          }
        }
      }, 100)

      // Load historical competitions with new period
      await this.loadHistoricalCompetitions()
    },
    async loadCompetitions() {
      this.isLoading = true
      this.loadingMessage = 'Loading competitions...'
      try {
        // Load active competitions
        const activeData = await competitionService.getActiveCompetitions()
        this.allCompetitions = activeData

        // Load available clubs for filter
        this.availableClubs = await competitionService.getAvailableClubs()

        // Apply filtering
        this.filterCompetitions(activeData)

        // Load historical competitions
        await this.loadHistoricalCompetitions()

        // Check scroll buttons after data is loaded
        this.$nextTick(() => {
          this.checkScrollButtons()
        })
      } catch (error) {
        console.error('Error loading competitions:', error)
      } finally {
        this.isLoading = false
      }
    },

    filterCompetitions(competitions) {
      // Filter by selected club if any
      let filtered = competitions
      if (this.selectedClubId !== null) {
        filtered = competitions.filter(c => c.clubId === this.selectedClubId)
      }

      // Split into live, demo, and handle 'ALL' type
      this.liveCompetitions = filtered.filter(c => c.mode === 'live' || c.mode === 'all')
      this.demoCompetitions = filtered.filter(c => c.mode === 'demo' || c.mode === 'all')
    },

    handleClubFilterChange() {
      // Re-filter competitions when club filter changes
      this.filterCompetitions(this.allCompetitions)
      this.$nextTick(() => {
        this.checkScrollButtons()
      })
    },

    async loadHistoricalCompetitions() {
      // No loading overlay for period changes - data already loaded
      try {
        this.historicalCompetitions = await competitionService.getHistoricalCompetitions(this.selectedPeriod)
        // Check scroll buttons after data is loaded
        this.$nextTick(() => {
          this.checkScrollButtonsHistorical()
        })
      } catch (error) {
        console.error('Error loading historical competitions:', error)
      }
    },

    handleScroll() {
      this.checkScrollButtons()
    },

    checkScrollButtons() {
      const container = this.$refs.carouselContainer
      if (!container) return

      this.canScrollLeft = container.scrollLeft > 0
      this.canScrollRight = container.scrollLeft < (container.scrollWidth - container.clientWidth - 10)
    },

    scrollLeft() {
      const container = this.$refs.carouselContainer
      if (!container) return

      container.scrollBy({
        left: -300,
        behavior: 'smooth'
      })
    },

    scrollRight() {
      const container = this.$refs.carouselContainer
      if (!container) return

      container.scrollBy({
        left: 424, // 400px card width + 24px gap
        behavior: 'smooth'
      })
    },

    handleScrollHistorical() {
      this.checkScrollButtonsHistorical()
    },

    checkScrollButtonsHistorical() {
      const container = this.$refs.historicalCarouselContainer
      if (!container) return

      this.canScrollLeftHistorical = container.scrollLeft > 0
      this.canScrollRightHistorical = container.scrollLeft < (container.scrollWidth - container.clientWidth - 10)
    },

    scrollLeftHistorical() {
      const container = this.$refs.historicalCarouselContainer
      if (!container) return

      container.scrollBy({
        left: -424, // 400px card width + 24px gap
        behavior: 'smooth'
      })
    },

    scrollRightHistorical() {
      const container = this.$refs.historicalCarouselContainer
      if (!container) return

      container.scrollBy({
        left: 424, // 400px card width + 24px gap
        behavior: 'smooth'
      })
    },

    createNewCompetition() {
      if (this.isReadOnlyStaff) return
      this.openWizard()
    },
    openWizard() {
      this.wizardOpen = true
      document.querySelector("html").classList.add("modal-open")
    },
    hideWizard() {
      this.wizardOpen = false
      this.fromWizard = true
      document.querySelector("html").classList.remove("modal-open")
      // Reload competitions after wizard closes
      this.loadCompetitions()
      // Reset fromWizard after animation completes
      setTimeout(() => {
        this.fromWizard = false
      }, 300)
    },

    viewCompetition(competition) {
      console.log('Viewing competition:', competition);
      // Clear any cached tournament data to ensure fresh data is loaded
      competitionService.clearTournamentCache(Number.parseInt(competition.id))
      // Also reset tournament state in Vuex store
      this.$store.commit('resetTournament')
      this.$router.push({
        name: 'broker-competition-rank',
        params: { competitionId: Number.parseInt(competition.id) }
      })
    },

    handleHistoricalClick(competition) {
      this.viewCompetition(competition);
    }
  }
}
</script>

<style lang="scss">
// Non-scoped styles for historical competitions grid alignment
.historical-competitions-section .row {
  margin-left: 0 !important;
  margin-right: 0 !important;

  >div[class*="col"] {
    padding: 12px !important;
  }

  // Large screens (3 cards per row)
  @media (min-width: 1264px) {
    >div.col-lg-4:nth-child(3n + 1) {
      padding-left: 0 !important;
    }

    >div.col-lg-4:nth-child(3n) {
      padding-right: 0 !important;
    }
  }

  // Medium screens (2 cards per row)
  @media (min-width: 960px) and (max-width: 1263px) {
    >div.col-md-6:nth-child(2n + 1) {
      padding-left: 0 !important;
    }

    >div.col-md-6:nth-child(2n) {
      padding-right: 0 !important;
    }
  }

  // Small screens (1 card per row)
  @media (max-width: 959px) {
    >div.col-12 {
      padding-left: 0 !important;
      padding-right: 0 !important;
    }
  }
}
</style>

<style lang="scss" scoped>
.broker-competitions {
  position: relative;
  min-height: 100vh; 
  .competitions-header {
    margin-bottom: 60px;

    .text-body-1 {
      line-height: 1.6 !important;
      max-width: 800px;
    }

    .btn-disabled {
      opacity: 0.5;
      cursor: not-allowed !important;
    }

    .create-competition-btn {
      border-radius: 24px !important;
      text-transform: none;
      font-weight: 500;
      font-size: 14px;
      padding: 0 10px !important;
      padding-left: 14px !important;
      margin-top: auto;
      border: 1px solid #005931 !important;
      background-color: transparent !important;
      color: #005931 !important;

      &:hover {
        background-color: rgba(0, 89, 49, 0.04) !important;
      }

      .v-icon {
        margin-right: 8px !important;
        color: #005931 !important;
        font-size: 16px !important;
      }
    }
  }

  .active-competitions-section {
    margin-bottom: 80px;
  }

  .period-filter-select {
    max-width: 250px;
    min-width: 200px;
  }


  .section-header {
    padding-bottom: 0px;

    .live-indicator {
      display: inline-block;
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background-color: #00C361;
      animation: flash 1.5s infinite;
      box-shadow: 0 0 0 0 rgba(0, 195, 97, 0.7);
    }

    @keyframes flash {
      0% {
        box-shadow: 0 0 0 0 rgba(0, 195, 97, 0.7);
        opacity: 1;
      }

      50% {
        box-shadow: 0 0 0 6px rgba(0, 195, 97, 0);
        opacity: 0.8;
      }

      100% {
        box-shadow: 0 0 0 0 rgba(0, 195, 97, 0);
        opacity: 1;
      }
    }
  }

  .mode-toggle-container {
    .mode-toggle-slider {
      position: relative;
      display: inline-flex;
      align-items: center;
      background: #F5F5F5;
      border-radius: 8px;
      padding: 0px;
      height: 40px;

      .slider-background {
        position: absolute;
        left: 2px;
        top: 2px;
        bottom: 2px;
        width: calc(50% - 2px);
        background: var(--broker-primary);
        border-radius: 7px;
        transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        z-index: 1;

        &.demo-active {
          transform: translateX(calc(100% - 2px));
        }
      }

      .toggle-option {
        position: relative;
        z-index: 2;
        display: flex;
        align-items: center;
        gap: 6px;
        padding: 0 20px;
        height: 36px;
        border: none;
        background: transparent;
        color: #000;
        font-size: 14px;
        font-weight: 500;
        font-family: inherit;
        cursor: pointer;
        transition: opacity 0.2s ease;
        min-width: 90px;
        justify-content: center;

        i {
          font-size: 14px;
          color: #000;
        }

        &:focus {
          outline: none;
        }
      }
    }
  }

  .competitions-carousel {
    .carousel-wrapper {
      position: relative;
      overflow: hidden;
    }

    .competition-cards-container {
      display: flex;
      gap: 24px;
      overflow-x: auto;
      scroll-behavior: smooth;
      padding: 4px 0 20px 0; // Bottom padding for scrollbar space
      scrollbar-width: none; // Firefox
      -ms-overflow-style: none; // IE/Edge

      &::-webkit-scrollbar {
        display: none; // Chrome/Safari
      }

      .competition-card-item {
        flex: 0 0 400px;
        max-width: 400px;
      }
    }

    /* // Right blur effect - progressive for smooth transition
    .carousel-blur-right {
      position: absolute;
      right: 0;
      top: 0;
      bottom: 20px; // Account for scrollbar padding
      width: 150px;
      pointer-events: none;
      z-index: 3;
      background: linear-gradient(
        90deg,
        rgba(255, 255, 255, 0) 0%,
        rgba(255, 255, 255, 0.1) 20%,
        rgba(245, 245, 245, 0.3) 50%,
        rgba(223, 223, 223, 0.5) 80%,
        rgba(248, 249, 250, 0.7) 100%
      );
      backdrop-filter: blur(4px);
      -webkit-backdrop-filter: blur(4px);
      // Create a mask for progressive blur effect
      mask-image: linear-gradient(
        90deg,
        transparent 0%,
        rgba(0, 0, 0, 0.3) 30%,
        rgba(0, 0, 0, 0.7) 60%,
        black 100%
      );
      -webkit-mask-image: linear-gradient(
        90deg,
        transparent 0%,
        rgba(0, 0, 0, 0.3) 30%,
        rgba(0, 0, 0, 0.7) 60%,
        black 100%
      );
    } */

    // Mobile responsive
    @media (max-width: 600px) {
      .competition-cards-container {
        .competition-card-item {
          flex: 0 0 100%;
          max-width: 100%;
        }
      }

      .carousel-blur-right {
        display: none;
      }
    }
  }

  .carousel-navigation {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 8px;

    .nav-btn {
      min-width: 28px;
      width: 28px;
      height: 28px;
      padding: 0;
      border-radius: 4px;

      &::before {
        display: none; // Remove Vuetify's ripple effect background
      }

      .v-btn__content {
        opacity: 1;
      }

      &:not(:disabled) {
        background: var(--broker-primary) !important;
        border: 1px solid var(--broker-primary);

        &:hover {
          background: var(--broker-primary) !important;
          filter: brightness(0.9);
        }

        .v-icon {
          color: black !important;
          font-size: 14px !important;
        }
      }

      &:disabled {
        background: #fafafa !important;
        border: none;

        .v-icon {
          color: #bdbdbd !important;
          font-size: 14px !important;
        }
      }
    }
  }

  // Filter button styling
  .filter-btn {
    min-width: 180px;
  }

  // Empty state styling
  .empty-state {
    text-align: center;
    padding: 48px 24px;
    background: transparent;
    border-radius: 12px;

    .v-icon {
      opacity: 0.5;
    }

    p {
      margin: 0;
      opacity: 0.7;
    }
  }
}

// Wizard Modal styling
.wizard-modal-backdrop {
  position: absolute;
  inset: 0; 
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 9998;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: fadeIn 0.3s ease-in-out;

  &.closing {
    animation: fadeOut 0.3s ease-in-out;
    animation-fill-mode: forwards;
    pointer-events: none;
  }
}

.wizard-modal-container {
  width: 100%;
  height: 100%;
  background-color: white;
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
}

.competitions-wizard {
  width: 100%;
  height: 100%;
  min-height: 100%;
  transform: translateX(100%);
  transition: transform 0.3s ease-in-out;
  background: white;


  &.three {
    transform: translateX(0);
  }

  &.three.out {
    animation: slideOut 0.3s ease-in-out;
    transform: translateX(100%);
  }
}

@keyframes slideOut {
  from {
    transform: translateX(0);
  }

  to {
    transform: translateX(100%);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

@keyframes fadeOut {
  from {
    opacity: 1;
  }

  to {
    opacity: 0;
  }
}
</style>